import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { artistsApi } from '../../../repositories/artists';
import { soundchartsEndpoints } from '../../../repositories/soundcharts';
import PhotoUpload from '../../../components/roster/PhotoUpload';
import Swal from 'sweetalert2';
import { AddArtistFormValues } from '../../../components/roster/types';
import MultiSelect from '../../../PitchFormPage/components/MultiSelect/MultiSelect';
import { dspDropdownOptions } from '../../../PitchFormPage/Data/dropdownOptions';
import Button from '../../../components/ui/Button';
import Breadcrumbs, { HOME_CRUMB } from '../../../components/ui/Breadcrumbs';


// Validation Schema (unchanged)
const addArtistValidationSchema = Yup.object().shape({
  artistName: Yup.string()
    .required('Artist name is required')
    .min(2, 'Artist name must be at least 2 characters')
    .max(255, 'Artist name must be less than 255 characters'),
  spotifyURI: Yup.string()
    .required('Spotify URI is required')
    .matches(
      /^(spotify:artist:|)?[a-zA-Z0-9]{22}$/,
      'Invalid Spotify URI format. Use either full URI or just the ID'
    ),
  appleID: Yup.string()
    .required('Apple ID is required')
    .matches(/^\d+$/, 'Apple ID must contain only numbers'),
  asin: Yup.string().required('ASIN is required'),
  soundcloudURL: Yup.string()
    .required('SoundCloud URL is required')
    .url('Invalid SoundCloud URL format'),
  tidalURL: Yup.string()
    .required('TIDAL URL is required')
    .url('Invalid TIDAL URL format'),
  bio: Yup.string().max(5000, 'Bio must be less than 5000 characters'),
  hometown: Yup.string().max(100, 'Hometown must be less than 100 characters'),
  currentTown: Yup.string().max(100, 'Current town must be less than 100 characters'),
  country: Yup.string().max(100, 'Country must be less than 100 characters'),
  label: Yup.string().max(100, 'Label must be less than 100 characters'),
  distributor: Yup.string().max(100, 'Distributor must be less than 100 characters'),
  instagram: Yup.string().url('Invalid Instagram URL format').nullable(),
  tiktok: Yup.string().url('Invalid TikTok URL format').nullable(),
  twitter: Yup.string().url('Invalid Twitter URL format').nullable(),
  facebook: Yup.string().url('Invalid Facebook URL format').nullable(),
  snapchat: Yup.string().url('Invalid Snapchat URL format').nullable(),
  spotifyURL: Yup.string().url('Invalid Spotify URL format').nullable(),
  appleURL: Yup.string().url('Invalid Apple Music URL format').nullable(),
  amazonURL: Yup.string().url('Invalid Amazon URL format').nullable(),
  deezerURL: Yup.string().url('Invalid Deezer URL format').nullable(),
  listenersLocation: Yup.string().nullable()
});

/**
 * On a failed submit, required-field errors can sit above the fold once the user has scrolled
 * down, leaving them with no indication the submit failed. This scrolls the first invalid
 * field into view and focuses it — once per submit. Every field's input id equals its Formik
 * field name, so the error key maps straight to the element.
 *
 * It must react to the submit, not to the errors: Formik re-validates on every keystroke, so
 * keying off `errors` alone pulled the cursor out of the field being typed in and into the next
 * invalid one (type two letters into Artist Name and focus jumped to Spotify URI).
 *
 * `isSubmitting` gates it because Formik bumps submitCount *before* validating; only once
 * isSubmitting drops back to false are the errors for that submit final.
 */
const ScrollToFirstError: React.FC = () => {
  const { submitCount, isSubmitting, errors } = useFormikContext<AddArtistFormValues>();
  const handledSubmitCount = useRef(0);

  useEffect(() => {
    if (isSubmitting || submitCount === handledSubmitCount.current) return;
    handledSubmitCount.current = submitCount;

    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) return;
    const el = document.getElementById(firstErrorKey);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus after the scroll so the field is both visible and ready for input. Guarded
      // because not every control (e.g. a custom widget) is focusable.
      (el as HTMLElement).focus?.({ preventScroll: true });
    }
  }, [submitCount, isSubmitting, errors]);

  return null;
};

const AddArtist: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [duplicateWarning, setDuplicateWarning] = useState<any>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  // Initial form values (unchanged)
  const initialValues: AddArtistFormValues = {
    artistName: '',
    spotifyURI: '',
    appleID: '',
    asin: '',
    soundcloudURL: '',
    tidalURL: '',
    artistUUID: '',
    bio: '',
    hometown: '',
    currentTown: '',
    country: '',
    label: '',
    distributor: '',
    instagram: '',
    tiktok: '',
    twitter: '',
    facebook: '',
    snapchat: '',
    spotifyURL: '',
    appleURL: '',
    amazonURL: '',
    deezerURL: '',
    listenersLocation: '',
    lyricLanguage: '',
    vevoRep: '',
    vevoRepOther: '',
    soundCloudContact: '',
    similarArtists: '',
  };

  // Handle photo selection (unchanged)
  const handlePhotoSelect = (file: File) => {
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle duplicate confirmation (unchanged)
  const handleDuplicateConfirmation = async (proceed: boolean, values: AddArtistFormValues) => {
    setShowDuplicateDialog(false);
    if (proceed) {
      await submitFormData(values, true); // Force submit despite duplicates
    }
  };

  // Submit form data (modified to handle scrolling on validation errors)
  const submitFormData = async (values: AddArtistFormValues, forceDuplicate = false) => {
    if (!forceDuplicate && duplicateWarning && duplicateWarning.duplicates.length > 0) {
      setShowDuplicateDialog(true);
      return;
    }

    try {
      setLoading(true);
      const submitData = new FormData();
      if (photoFile) {
        submitData.append('photo', photoFile);
      }
      submitData.append('artistName', values.artistName.trim());
      submitData.append('spotifyURI', values.spotifyURI.trim());
      submitData.append('appleID', values.appleID.trim());
      submitData.append('asin', values.asin.trim());
      submitData.append('soundcloudURL', values.soundcloudURL.trim());
      submitData.append('tidalURL', values.tidalURL.trim());
      if (values.artistUUID) submitData.append('artistUUID', values.artistUUID.trim());
      if (values.bio.trim()) submitData.append('bio', values.bio.trim());
      if (values.hometown.trim()) submitData.append('hometown', values.hometown.trim());
      if (values.currentTown.trim()) submitData.append('currentTown', values.currentTown.trim());
      if (values.country.trim()) submitData.append('country', values.country.trim());
      if (values.label.trim()) submitData.append('label', values.label.trim());
      if (values.distributor.trim()) submitData.append('distributor', values.distributor.trim());
      if (values.instagram.trim()) submitData.append('instagram', values.instagram.trim());
      if (values.tiktok.trim()) submitData.append('tiktok', values.tiktok.trim());
      if (values.twitter.trim()) submitData.append('twitter', values.twitter.trim());
      if (values.facebook.trim()) submitData.append('facebook', values.facebook.trim());
      if (values.snapchat.trim()) submitData.append('snapchat', values.snapchat.trim());
      if (values.spotifyURL.trim()) submitData.append('spotifyURL', values.spotifyURL.trim());
      if (values.appleURL.trim()) submitData.append('appleURL', values.appleURL.trim());
      if (values.amazonURL.trim()) submitData.append('amazonURL', values.amazonURL.trim());
      if (values.deezerURL.trim()) submitData.append('deezerURL', values.deezerURL.trim());
      // Add this line to append listenersLocation as string
      if (values.listenersLocation && values.listenersLocation.trim()) {
        submitData.append('listenersLocation', values.listenersLocation.trim());
      }
      if (values.lyricLanguage && values.lyricLanguage.trim()) {
        submitData.append('lyricLanguage', values.lyricLanguage.trim());
      }
      if (values.vevoRep && values.vevoRep.trim()) {
        submitData.append('vevoRep', values.vevoRep.trim());
      }
      if (values.vevoRepOther && values.vevoRepOther.trim()) {
        submitData.append('vevoRepOther', values.vevoRepOther.trim());
      }
      if (values.soundCloudContact && values.soundCloudContact.trim()) {
        submitData.append('soundCloudContact', values.soundCloudContact.trim());
      }
      if (values.similarArtists && values.similarArtists.trim()) {
        submitData.append('similarArtists', values.similarArtists.trim());
      }

      const response = await artistsApi.create(submitData);
      const newArtist = response.data.data || response.data;

      Swal.fire({
        title: 'Success!',
        text: `${newArtist.artistName} has been added to your roster.`,
        icon: 'success',
        confirmButtonText: 'OK',
      });

      navigate('/roster');
    } catch (error: any) {
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        let errorMessage = 'Validation failed:\n';
        validationErrors.forEach((err: any) => {
          errorMessage += `• ${err}\n`;
        });

        Swal.fire({
          title: 'Validation Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
        });

        // Scroll to the first error field after showing the alert
        setTimeout(() => {
          const errorElement = document.querySelector('.border-danger-500, .text-danger-600, .border-red-500, .text-red-600');
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        }, 200);
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to create artist. Please try again.';
        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: AddArtistFormValues, { setSubmitting }: any) => {
    // Check for duplicates before submitting
    if (duplicateWarning && duplicateWarning.duplicates.length > 0) {
      setShowDuplicateDialog(true);
      setSubmitting(false);
      return;
    }

    await submitFormData(values);
    setSubmitting(false);
  };
  // Custom input component (unchanged)
  const InputField = ({
    name,
    label,
    placeholder,
    type = 'text',
    required = false,
    maxLength,
    rows,
  }: {
    name: string;
    label: string;
    placeholder: string;
    type?: string;
    required?: boolean;
    maxLength?: number;
    rows?: number;
  }) => (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-neutral-700">
        {label} {required && <span className="text-danger-600">*</span>}
      </label> 
      <Field name={name}>
        {({ field, form, meta }: any) => (
          <>
            {type === 'textarea' ? (
              <textarea
                {...field}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                className={`w-full px-3 py-2 border rounded-md outline-none transition-colors placeholder-neutral-400 focus:border-primary-500 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'
                  }`}
                onChange={(e) => {
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }
                  field.onChange(e);
                }}
              />
            ) : (
              <input
                {...field}
                type={type}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                className={`w-full px-3 py-2 border rounded-md outline-none transition-colors placeholder-neutral-400 focus:border-primary-500 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'
                  }`}
                onChange={(e) => {
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }
                  field.onChange(e);
                }}
              />
            )}
            {name === 'bio' && (
              <div className="text-sm text-neutral-500">
                {field.value.length}/5000 characters
              </div>
            )}
            {name === 'spotifyURI' && (
              <p className="mt-1 text-xs text-neutral-500">e.g., spotify:artist:5zixe6AbgXPqt4c1uSl94L <span className="font-semibold text-neutral-700">OR</span> 5zixe6AbgXPqt4c1uSl94L</p>
            )}
            <ErrorMessage name={name} component="div" className="mt-1 text-sm text-danger-600" />
          </>
        )}
      </Field>
    </div>
  );

  return (
    <div className="py-2 sm:p-6">
      <div className="mx-auto max-w-7xl">
        <Breadcrumbs
          items={[HOME_CRUMB, { label: 'Roster', to: '/roster' }, { label: 'Add Artist' }]}
        />
        <div className="bg-white border rounded-lg border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-100" data-tour="add-artist-form-header">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold tracking-tight text-black sm:text-2xl">Add New Artist</h1>
              <button
                onClick={() => navigate('/roster')}
                aria-label="Close"
                className="border border-transparent p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={addArtistValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched }) => {
              // Duplicate checking effect (unchanged)
              React.useEffect(() => {
                const checkDuplicates = async () => {
                  if (values.artistName.trim() && values.artistName.length >= 2) {
                    try {
                      const response = await artistsApi.checkDuplicates(
                        values.artistName,
                        values.spotifyURI || undefined
                      );
                      if (response.data.data.duplicates && response.data.data.duplicates.length > 0) {
                        setDuplicateWarning(response.data.data);
                      } else {
                        setDuplicateWarning(null);
                      }
                    } catch (error) {
                      setDuplicateWarning(null);
                    }
                  } else {
                    setDuplicateWarning(null);
                  }
                };

                const timeoutId = setTimeout(checkDuplicates, 500);
                return () => clearTimeout(timeoutId);
              }, [values.artistName, values.spotifyURI]);

              return (
                <Form className="p-6 space-y-6">
                  <ScrollToFirstError />
                  {duplicateWarning && duplicateWarning.duplicates.length > 0 && (
                    <div className="p-4 mb-6 border rounded-lg border-warning-200 bg-warning-50">
                      <div className="flex items-start space-x-3">
                        <svg
                          className="w-5 h-5 text-warning-500 mt-0.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-warning-800">
                            Potential Duplicates Detected
                          </h3>
                          <div className="mt-2 text-sm text-warning-800">
                            {duplicateWarning.duplicates.map((duplicate: any, index: number) => (
                              <div key={index} className="mb-1">
                                <span className="ml-2 text-xs">
                                  (Existing: {duplicate.artistName} - Spotify ID: {duplicate.spotifyURI || 'N/A'})
                                </span>

                              </div>
                            ))}
                          </div>
                          <p className="mt-2 text-xs text-warning-700">
                            Please review and confirm if you want to proceed with adding this artist.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {showDuplicateDialog && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl">
                        <h3 className="mb-4 text-lg font-semibold text-black">
                          Confirm Duplicate Artist
                        </h3>
                        <p className="mb-4 text-sm text-neutral-600">
                          Similar artists were found in your roster. Are you sure you want to add this
                          artist?
                        </p>
                        <div className="mb-4 text-sm text-neutral-600">
                          {duplicateWarning?.duplicates.map((duplicate: any, index: number) => (
                            <div key={index} className="mb-1">
                              • {duplicate.artistName} (Spotify ID: {duplicate.spotifyURI || 'N/A'})
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button
                            variant="neutral"
                            onClick={() => handleDuplicateConfirmation(false, values)}
                          >
                            Cancel
                          </Button>
                          <Button onClick={() => handleDuplicateConfirmation(true, values)}>
                            Add Anyway
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-neutral-700">
                      Artist Photo
                    </label>
                    <PhotoUpload onPhotoSelect={handlePhotoSelect} preview={photoPreview} />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-black">Required Information</h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      <InputField
                        name="artistName"
                        label="Artist Name"
                        placeholder="Enter Artist Name"
                        required
                      />
                      <InputField
                        name="spotifyURI"
                        label="Spotify URI"
                        placeholder="25BObiRSDCMwVrBGIVaLIf"
                        required
                      />
                      <InputField
                        name="appleID"
                        label="Apple ID"
                        placeholder="123456789"
                        required
                      />

                      <InputField
                        name="asin"
                        label="ASIN"
                        placeholder="B07XVQZXYZ"
                        required
                      />
                      <InputField
                        name="soundcloudURL"
                        label="SoundCloud URL"
                        placeholder="https://soundcloud.com/artist-name"
                        type="url"
                        required
                      />
                      <InputField
                        name="tidalURL"
                        label="TIDAL URL"
                        placeholder="https://tidal.com/artist/artist-name"
                        type="url"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-black">Optional Information</h3>
                    <InputField
                      name="bio"
                      label="Bio"
                      placeholder="Enter artist bio..."
                      type="textarea"
                      maxLength={5000}
                      rows={4}
                    />
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      <InputField
                        name="hometown"
                        label="Artist Hometown"
                        placeholder="Enter hometown"
                        maxLength={100}
                      />
                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-700">
                          Lyric Language
                        </label>
                        <Field name="lyricLanguage">
                          {({ field, form, meta }: any) => (
                            <>
                              <MultiSelect
                                options={dspDropdownOptions.language}
                                value={field.value ? [field.value] : []}
                                onChange={(value) => {
                                  const stringValue = value && value.length > 0 ? value[0] : '';
                                  form.setFieldValue("lyricLanguage", stringValue);
                                }}
                                placeholder="Select Language"
                                maxSelections={1}
                              />
                              <ErrorMessage name="lyricLanguage" component="div" className="mt-1 text-sm text-danger-600" />
                            </>
                          )}
                        </Field>
                      </div>
                      <InputField
                        name="currentTown"
                        label="Current Town"
                        placeholder="Enter current town"
                        maxLength={100}
                      />
                      <InputField
                        name="country"
                        label="Country"
                        placeholder="Enter country"
                        maxLength={100}
                      />
                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-700">
                          Location of Listeners 
                        </label>
                        <Field name="listenersLocation">
                          {({ field, form, meta }: any) => (
                            <>
                              <MultiSelect
                                options={dspDropdownOptions.listenersLocation}
                                value={field.value ? [field.value] : []} // Convert string to array for MultiSelect
                                onChange={(value) => {
                                  // Convert array back to string (take first selected value)
                                  const stringValue = value && value.length > 0 ? value[0] : '';
                                  form.setFieldValue("listenersLocation", stringValue);
                                }}
                                placeholder="Select Location"
                                maxSelections={1}
                                required={true}
                              />
                              <ErrorMessage name="listenersLocation" component="div" className="mt-1 text-sm text-danger-600" />
                            </>
                          )}
                        </Field>
                      </div>

                      <InputField
                        name="label"
                        label="Label"
                        placeholder="Enter label"
                        maxLength={100}
                      />
                      <InputField
                        name="distributor"
                        label="Distributor"
                        placeholder="Enter distributor"
                        maxLength={100}
                      />
                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-700">
                          Vevo Rep
                        </label>
                        <Field name="vevoRep">
                          {({ field, form, meta }: any) => (
                            <>
                              <div className="relative">
                                <select
                                  {...field}
                                  className={`w-full px-3 py-2 text-sm border rounded-md outline-none transition-colors focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'}`}
                                  onChange={(e) => form.setFieldValue('vevoRep', e.target.value)}
                                >
                                  <option value="">Select Vevo Rep</option>
                                  {dspDropdownOptions.vevoRep.map((rep: string) => (
                                    <option key={rep} value={rep}>{rep}</option>
                                  ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                              </div>
                              <ErrorMessage name="vevoRep" component="div" className="mt-1 text-sm text-danger-600" />
                            </>
                          )}
                        </Field>
                      </div>
                      {values.vevoRep === 'Other' && (
                        <InputField
                          name="vevoRepOther"
                          label="Specify Vevo Rep Name"
                          placeholder="Enter Vevo Rep name"
                        />
                      )}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-neutral-700">
                          SoundCloud Contact
                        </label>
                        <Field name="soundCloudContact">
                          {({ field, form, meta }: any) => (
                            <>
                              <div className="relative">
                                <select
                                  {...field}
                                  className={`w-full px-3 py-2 text-sm border rounded-md outline-none transition-colors focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'}`}
                                  onChange={(e) => form.setFieldValue('soundCloudContact', e.target.value)}
                                >
                                  <option value="">Select Contact</option>
                                  {dspDropdownOptions.soundCloudContact.map((contact: string) => (
                                    <option key={contact} value={contact}>{contact}</option>
                                  ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </div>
                              </div>
                              <ErrorMessage name="soundCloudContact" component="div" className="mt-1 text-sm text-danger-600" />
                            </>
                          )}
                        </Field>
                      </div>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-neutral-700">
                        Similar Artists
                      </label>
                      <Field name="similarArtists">
                        {({ field, form }: any) => {
                          const currentArtists: string[] = field.value
                            ? field.value.split(',').map((a: string) => a.trim()).filter((a: string) => a)
                            : [];
                          const isMaxReached = currentArtists.length >= 3;
                          const [inputVal, setInputVal] = React.useState('');
                          const addArtist = () => {
                            const newArtist = inputVal.trim();
                            if (newArtist && !currentArtists.includes(newArtist) && currentArtists.length < 3) {
                              form.setFieldValue('similarArtists', [...currentArtists, newArtist].join(', '));
                              setInputVal('');
                            }
                          };
                          return (
                            <div className="w-full bg-white border rounded-md border-neutral-300 focus-within:border-primary-500">
                              {currentArtists.length > 0 && (
                                <div className="flex flex-wrap gap-2 p-3 border-b border-neutral-100">
                                  {currentArtists.map((artist: string, index: number) => (
                                    <span key={index} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full gap-2 text-primary-700 bg-primary-50 border border-primary-200">
                                      <span>{artist}</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...currentArtists];
                                          updated.splice(index, 1);
                                          form.setFieldValue('similarArtists', updated.join(', '));
                                        }}
                                        className="flex items-center justify-center w-4 h-4 transition-colors rounded-full text-primary-700 hover:bg-primary-200 hover:text-primary-800"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="relative flex items-center">
                                <input
                                  type="text"
                                  value={inputVal}
                                  onChange={(e) => setInputVal(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addArtist(); } }}
                                  placeholder={isMaxReached ? 'Maximum 3 artists selected' : 'Type artist name and press Enter'}
                                  disabled={isMaxReached}
                                  className="flex-1 px-3 py-2 text-sm bg-transparent border-none outline-none text-neutral-700 placeholder-neutral-400 disabled:text-neutral-400 disabled:cursor-not-allowed"
                                />
                                {!isMaxReached && inputVal.trim() && (
                                  <button type="button" onClick={addArtist} className="flex items-center justify-center w-8 h-8 mr-2 transition-colors rounded-full text-primary-700 hover:bg-primary-50">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }}
                      </Field>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-neutral-500">
                          {(values.similarArtists ? values.similarArtists.split(',').map((a: string) => a.trim()).filter((a: string) => a) : []).length}/3 artists selected
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="mb-3 font-semibold text-black text-md">Social Media</h4>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <InputField
                          name="instagram"
                          label="Instagram"
                          placeholder="https://instagram.com/username"
                          type="url"
                        />
                        <InputField
                          name="tiktok"
                          label="TikTok"
                          placeholder="https://tiktok.com/@username"
                          type="url"
                        />
                        <InputField
                          name="twitter"
                          label="X"
                          placeholder="https://x.com/username"
                          type="url"
                        />
                        <InputField
                          name="facebook"
                          label="Facebook"
                          placeholder="https://facebook.com/username"
                          type="url"
                        />
                        <InputField
                          name="snapchat"
                          label="Snapchat"
                          placeholder="https://snapchat.com/add/username"
                          type="url"
                        />
                      </div>
                    </div>
                    <div>
                      <h4 className="mb-3 font-semibold text-black text-md">Streaming URLs</h4>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <InputField
                          name="spotifyURL"
                          label="Spotify URL"
                          placeholder="https://open.spotify.com/artist/..."
                          type="url"
                        />
                        <InputField
                          name="appleURL"
                          label="Apple URL"
                          placeholder="https://music.apple.com/artist/..."
                          type="url"
                        />
                        <InputField
                          name="amazonURL"
                          label="Amazon URL"
                          placeholder="https://amazon.com/artist/..."
                          type="url"
                        />
                        <InputField
                          name="deezerURL"
                          label="Deezer URL"
                          placeholder="https://deezer.com/artist/..."
                          type="url"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <Button type="button" variant="neutral" onClick={() => navigate('/roster')}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={loading} data-tour="add-artist-submit-btn">
                      {loading ? 'Adding Artist...' : 'Add Artist'}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default AddArtist;